/* eslint-disable react/jsx-no-bind */
import React, { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { openDashboardDialog } from '@actions/dashboard-actions';
import {
  fetchGroupBoundaryNotifications,
  getConfirmableDeleteBoundaryNotifications,
  clearNotification
} from '@actions/groups-actions';
import { showConfirmationDialog } from '@actions/confirmation-actions';
import TableHeader from '@components/entity/table-header';
import * as dialog from '@constants/dialogs';
import { NOTIFICATIONS_TAB_COLUMNS as columns } from '@constants/table';
import CreateNotification from '@components/group/dialogs/create-notification';
import Table from '@shared/table';

const empty = {
  icon: <NotificationsNoneIcon htmlColor="#E0E0E0" style={{ fontSize: '6rem' }} />,
  message: 'No notifications created',
  subText: <span>Send entities in this boundary group as CSV attachments via email to other users.</span>
};

const Notifications = () => {
  const dispatch = useDispatch();
  const { loading, notifications } = useSelector(state => state.groups);
  const { data } = useSelector(state => state.dataDetail);

  useEffect(() => {
    const fetchNotifications = async () => dispatch(fetchGroupBoundaryNotifications(data?.id));
    fetchNotifications();
  }, [data?.id, dispatch]);  // eslint-disable-line react-hooks/exhaustive-deps
  const rows = useMemo(() => notifications || [], [notifications]);

  const openNotificationDialog = useCallback(() => {
    dispatch(clearNotification());
    dispatch(openDashboardDialog(dialog.CREATE_NOTIFICATION, { group: data?.id }));
  }, [dispatch, data?.id]);

  const editNotification = useCallback(
    row => dispatch(openDashboardDialog(dialog.CREATE_NOTIFICATION, { data: row, group: data?.id })),
    [dispatch, data?.id]
  );

  const deleteNotification = useCallback(row => {
    dispatch(showConfirmationDialog(
      'Delete notification?',
      'Are you sure you want to delete this notification?',
      getConfirmableDeleteBoundaryNotifications(row.id)
    ));
  }, [dispatch]);

  const actions = useMemo(() => {
    const actionList = [
      { icon: EditIcon, label: 'Edit', onClick: editNotification },
      { icon: DeleteIcon, label: 'Delete', onClick: deleteNotification }
    ];
    return actionList;
  }, [editNotification, deleteNotification]);

  const header = useMemo(() => (
    <TableHeader
      count={rows.length}
      label="notification"
      onAdd={openNotificationDialog}
    />
  ), [openNotificationDialog, rows]);

  if (!data) {
    return null;
  }

  return (
    <Fragment>
      <Table
        actions={actions}
        columns={columns}
        empty={empty}
        header={header}
        loading={loading}
        rows={rows}
        style={{ maxWidth: '55rem' }}
      />
      <CreateNotification />
    </Fragment>
  );
};

export default memo(Notifications);
